import axios from 'axios';
import { startLoading, stopLoading } from '@app/loading/loadingBridge';
import { notify } from '@app/notifications/notifyBridge';

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL, // <-- use VITE_API_URL (not VITE_url)
  withCredentials: true,     
  withXSRFToken: true,            // send/receive cookies for Sanctum
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }
});

const shouldTrack = (config) => !config?.meta?.silent;

// Add request interceptor for debugging
http.interceptors.request.use(request => {
  if (shouldTrack(request)) startLoading();
  // console.log('Making request to:', request.baseURL + request.url);
  // console.log('Request headers:', request.headers);
  return request;
});

// Add response interceptor — handles global error cases (401/403/5xx) so every
// component doesn't have to. 4xx (other than 401/403) is left for components
// to handle with context-aware messages. See docs/infrastructure/error-handling.md.
//
// Opt-out: pass `meta: { silent: true }` on the axios config to suppress the
// global toast/redirect (component handles it locally).
http.interceptors.response.use(
  response => {
    if (shouldTrack(response.config)) stopLoading();
    return response;
  },
  error => {
    if (shouldTrack(error?.config)) stopLoading();

    const status  = error.response?.status;
    const data    = error.response?.data ?? {};
    const traceId = data.trace_id;
    const silent  = error.config?.meta?.silent;

    // Always log the failure — helps debugging without changing UX
    // eslint-disable-next-line no-console
    console.error('[http]', error.config?.method?.toUpperCase(), error.config?.url, status, data);

    if (!silent) {
      if (status === 401) {
        // Session expired — kick to login. Skip on the login route itself
        // so failed-login attempts don't redirect away from their own error.
        const onLogin = window.location.pathname.startsWith('/login');
        if (!onLogin) {
          window.location.href = '/login';
        }
      } else if (status === 403) {
        notify.error('You do not have permission to do that.');
      } else if (status >= 500) {
        notify.error(
          traceId
            ? `Server error. Reference: ${traceId}`
            : 'Server error — please try again or contact support.'
        );
      }
      // 4xx other than 401/403 (validation, business rules): caller handles
    }

    return Promise.reject(error);
  }
);

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
http.defaults.xsrfCookieName = 'XSRF-TOKEN';
http.defaults.xsrfHeaderName  = 'X-XSRF-TOKEN';
